import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';

import axios, { fetcher, endpoints } from 'src/utils/axios';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

const URL = endpoints.subscription;

const options = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

/**
 * Get all subscriptions
 * @returns an array of subscriptions
 */
export function useGetSubscriptions() {
  const { user } = useAuthContext();

  const { data, isLoading, error, isValidating } = useSWR(
    user?.isSuperAdmin ? URL : null,
    fetcher,
    options
  );

  const memoizedValue = useMemo(() => {
    const subscriptions = data?.subscriptions?.map((subscription) => ({
      ...subscription,
    }));
    return {
      subscriptions: subscriptions || [],
      subscriptionsLoading: isLoading,
      subscriptionsError: error,
      subscriptionsValidating: isValidating,
      subscriptionsEmpty: !isLoading && !data?.subscriptions?.length,
      subscriptionsLength: data?.subscriptions?.length || 0,
    };
  }, [data, error, isLoading, isValidating]);

  return memoizedValue;
}

// ----------------------------------------------------------------------

/**
 * Get a subscription by id
 * @param {Number} id
 * @returns a subscription by id
 */
export function useGetSubscriptionById(id) {
  const { data, isLoading, error, isValidating } = useSWR(
    id ? `${URL}/${id}` : null,
    fetcher,
    options
  );

  const memoizedValue = useMemo(() => {
    if (!data) return false;
    const { subscription } = data;
    return {
      subscription: subscription || {},
      subscriptionLoading: isLoading,
      subscriptionError: error,
      subscriptionValidating: isValidating,
      subscriptionEmpty: !isLoading && !subscription?.length,
      subscriptionsLength: subscription?.length || 0,
    };
  }, [data, error, isLoading, isValidating]);

  return memoizedValue;
}

// ----------------------------------------------------------------------

/**
 * Get subscription by user
 * @returns subscriptions by user
 */
export function useGetSubscriptionByUser() {

  const { user } = useAuthContext();

  const { data, isLoading, error, isValidating } = useSWR(
    user?.id ? `${URL}/user` : null,
    fetcher,
    options
  );

  const memoizedValue = useMemo(() => {
    if (!data) return false;
    const { subscription } = data;
    return {
      subscription: subscription || {},
      subscriptionLoading: isLoading,
      subscriptionError: error,
      subscriptionValidating: isValidating,
      subscriptionEmpty: !isLoading && !data?.subscription?.length,
      subscriptionLength: data?.length || 0,
    };
  }, [data, error, isLoading, isValidating]);

  return memoizedValue;
}

// ----------------------------------------------------------------------

/**
 * Get subscription by user
 * @returns subscriptions by user
 */
export function useGetSubscriptionUrlsByUser() {
  const { user } = useAuthContext();

  const { data, isLoading, error, isValidating, mutate: refetchUrls } = useSWR(
    user?.id ? `${URL}/user/urls` : null,
    fetcher,
    options
  );

  const memoizedValue = useMemo(() => {
    if (!data) return false;
    const { urls } = data;
    return {
      urls: urls || {},
      urlsLoading: isLoading,
      urlsError: error,
      urlsValidating: isValidating,
      urlsEmpty: !isLoading && !data?.urls?.length,
      urlsLength: data?.length || 0,
      refetchUrls,
    };
  }, [data, error, isLoading, isValidating, refetchUrls]);

  return memoizedValue;
}

// ----------------------------------------------------------------------

/**
 * Change a subscription by variant id
 * @param {Number} variantId lemonSqueezy id
 */
export async function changeSubscriptionPlan(variantId) {
  await axios.put(`${URL}/change`, { variantId });
  await mutate(`${URL}/user`);
}

// ----------------------------------------------------------------------

/**
 * Change a subscription by lemonSqueezy id
 * @param {Number} id lemonSqueezy id
 * @param {Number} variantId
 */
export async function changeSubscriptionPlanForAdmins(id, variantId) {
  await axios.put(id ? `${URL}/plan/admin/${id}` : '', { variantId });
}

// ----------------------------------------------------------------------

/**
 * Pause a subscription by id
 * @param {Number} id
 */
export async function pauseSubscriptionPlan(id) {
  await axios.put(id ? `${URL}/pause/${id}` : null);
}

// ----------------------------------------------------------------------

/**
 * Resume a subscription by id
 * @param {Number} id
 */
export async function resumeSubscriptionPlan(id) {
  await axios.put(id ? `${URL}/resume/${id}` : null);
}

// ----------------------------------------------------------------------

/**
 * Renew a subscription by id
 */
export async function renewSubscriptionPlan() {
  await axios.put(`${URL}/renew`);
  await mutate(`${URL}/user`);
}

// ----------------------------------------------------------------------

/**
 * Cancel a subscription by id
 */
export async function cancelSubscriptionPlan() {
  await axios.put(`${URL}/cancel`);
  await mutate(`${URL}/user`);
}
